import { InspectionStatus } from './components/StatusChip';

/**
 * StatusMap — translates the backend/mobile status vocabularies into the
 * design-system StatusKey used by <StatusChip>/<StatusDot>/<StatusSegment>.
 *
 * Two overlapping status models live in the schema (see CLAUDE.md):
 *   - new model (apartment_inspection_records.attempts): brak_uwag / zalecenie /
 *     brak_dostepu / odmowa_lokatora / odmowa_wpisu
 *   - legacy model (apartments.inspection_status): not_scheduled / scheduled /
 *     completed / no_access / refused
 *
 * The DS StatusKey ('ok' | 'rec' | 'noaccess' | 'deny' | 'todo') is the same
 * union as InspectionStatus exported by StatusChip — re-exported here as the
 * canonical name so callers can `import { StatusKey } from '../ds/StatusMap'`.
 */
export type StatusKey = InspectionStatus;

/**
 * Source status string (new + legacy vocabularies) → DS StatusKey.
 * Keys are the raw strings as stored in the DB / sent by mobile.
 */
export const STATUS_MAP: Record<string, StatusKey> = {
  // --- new model (attempts JSONB) ---
  brak_uwag:       'ok',
  zalecenie:       'rec',
  brak_dostepu:    'noaccess',
  odmowa_lokatora: 'deny',
  odmowa_wpisu:    'deny',

  // --- legacy model (apartments.inspection_status) ---
  not_scheduled:   'todo',
  scheduled:       'todo',
  completed:       'ok',
  no_access:       'noaccess',
  refused:         'deny',
};

/**
 * statusToDS — resolve any backend/mobile status string to a DS StatusKey.
 * Unknown / empty values fall back to 'todo' (neutral "do zrobienia").
 */
export function statusToDS(s: string): StatusKey {
  return STATUS_MAP[s] ?? 'todo';
}

/**
 * Terminal/open/has-inventory vocabulary — MIRROR of
 * backend/src/utils/apartmentStatus.js (pinned by backend parity.test.js).
 * Keep these three sets + canonStatus in sync; do NOT fork (spec §2.2).
 */
export const TERMINAL_STATUSES = new Set(['brak_uwag', 'zalecenie', 'odmowa_wpisu']);
export const OPEN_STATUSES = new Set(['brak_dostepu', 'odmowa_lokatora']);
export const HAS_INVENTORY_STATUSES = new Set(['brak_uwag', 'zalecenie']);

const LEGACY_TO_CANON: Record<string, string | null> = {
  completed: 'brak_uwag',
  no_access: 'brak_dostepu',
  refused: 'odmowa_lokatora',
  not_scheduled: null,
  scheduled: null,
};

export function canonStatus(air?: string | null, legacy?: string | null): string | null {
  if (air) return air;
  if (legacy && Object.prototype.hasOwnProperty.call(LEGACY_TO_CANON, legacy)) return LEGACY_TO_CANON[legacy];
  return null;
}
export function isTerminal(s?: string | null): boolean { return s != null && TERMINAL_STATUSES.has(s); }
export function isOpen(s?: string | null): boolean { return s != null && OPEN_STATUSES.has(s); }
export function botShouldSkip(s?: string | null): boolean { return s == null || !HAS_INVENTORY_STATUSES.has(s); }

// ── ETYKIETY UI — LUSTRO backend/src/utils/apartmentStatus.js (pinowane parity-testem) ──
// Właściciel 2026-07-28: „żeby statusy były jednakowe w adminie i mobilce, żeby nie było
// różnic". Mapa obsługuje OBA słowniki (kanoniczny z AIR i legacy z apartments), bo różne
// ekrany dostają różne wartości.
// ⚠️ `completed` NIE jest „Brak uwag": legacy mapuje na niego i brak_uwag, i zalecenie —
// „Brak uwag" twierdziłoby, że uwag nie było, czasem nieprawdziwie.
export const STATUS_LABELS: Record<string, string> = {
  brak_uwag: 'Brak uwag',
  zalecenie: 'Zalecenia',
  brak_dostepu: 'Nieobecność',
  odmowa_lokatora: 'Odmowa',
  odmowa_wpisu: 'Odmowa',
  completed: 'Wykonano',
  no_access: 'Nieobecność',
  refused: 'Odmowa',
  scheduled: 'Zaplanowano',
  // Właściciel 2026-10-02 (docx): lokal bez wyniku wizyty wyglądał jak „niezaplanowany" mimo
  // zaplanowanych terminów budynku — etykieta mówi teraz, co faktycznie wiemy: nikt go nie sprawdził.
  // Klucz bez zmian; zmiana we WSZYSTKICH lustrach naraz (statusLabelsParity.test.js).
  not_scheduled: 'Niesprawdzony',
};

// Statusy niosące WYNIK WIZYTY — tylko przy nich pokazujemy datę wykonania. Dla
// „nie zaplanowano"/„zaplanowano" data pochodziłaby z poprzedniego cyklu i myliłaby.
const NO_VISIT = new Set(['not_scheduled', 'scheduled']);

export function statusLabel(s?: string | null): string {
  return (s != null && STATUS_LABELS[s]) || STATUS_LABELS.not_scheduled;
}
export function hasVisitOutcome(s?: string | null): boolean {
  return s != null && s !== '' && !NO_VISIT.has(s);
}
